import { useEffect, useMemo, useState } from "react";
import ConversationListPanel from "../components/ConversationListPanel/ConversationListPanel";
import ConversationFiltersPanel from "../components/ConversationFiltersPanel/ConversationFiltersPanel";
import PhoneCallsPanel from "../components/PhoneCallsPanel/PhoneCallsPanel";
import { Page } from "@iq/models";
import "../styles/conversationPage.css";
import { getContacts, getDeals } from "../services/contact_service";
import { EMAIL_FOLDERS, PANEL_TYPE_MAP, PHONE_FOLDERS } from "./conversation/constants";
import { createConversationHandlers } from "./conversation/handlers";
import {
    disconnectGraph,
    getConversationMessages,
    getConversations,
    getContactMessages,
    getPhoneCalls,
    getGraphConnectURL,
    getGraphStatus,
    sendGraphMail,
} from "../services/conversation_service";
import { FiBarChart2, FiMessageSquare, FiUsers } from "react-icons/fi";
import PerformanceWidget from "../components/PerformanceWidget/PerformanceWidget";
import ContactsWidget from "../components/ContactsWidget/ContactsWidget";
import { addAccountIdentity } from "./conversation/conversationIdentity";

function Conversations(){
    const [selectedFolder, setSelectedFolder] = useState("all");
    const [conversations, setConversations] = useState([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [conversationsError, setConversationsError] = useState("");
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messagesError, setMessagesError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [graphStatus, setGraphStatus] = useState(null);
    const [graphStatusLoading, setGraphStatusLoading] = useState(false);
    const [graphStatusError, setGraphStatusError] = useState("");
    const [connectingGraph, setConnectingGraph] = useState(false);
    const [disconnectingGraph, setDisconnectingGraph] = useState(false);
    const [isComposeMode, setIsComposeMode] = useState(false);
    const [sendingMail, setSendingMail] = useState(false);
    const [mailFeedback, setMailFeedback] = useState("");
    const [mailForm, setMailForm] = useState({
        to: "",
        subject: "",
        body: "",
    });
    const [phoneCalls, setPhoneCalls] = useState([]);
    const [phoneMetrics, setPhoneMetrics] = useState({
        my_daily: 0,
        my_weekly: 0,
        team_daily: 0,
        team_weekly: 0,
        daily_goal: 60,
        weekly_goal: 300,
    });
    const [loadingPhoneCalls, setLoadingPhoneCalls] = useState(false);
    const [phoneCallsError, setPhoneCallsError] = useState("");
    const [deals, setDeals] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [historyContactId, setHistoryContactId] = useState(null);
    const [contactMessages, setContactMessages] = useState([]);
    const [loadingContactMessages, setLoadingContactMessages] = useState(false);
    const [contactMessagesError, setContactMessagesError] = useState("");

    const [currentView, setCurrentView] = useState("conversations");
    const folderLabelMap = useMemo(() => {
        return [...EMAIL_FOLDERS, ...PHONE_FOLDERS].reduce((acc, folder) => {
            acc[folder.id] = folder.label;
            return acc;
        }, {});
    }, []);

    const selectedPanelType = PANEL_TYPE_MAP[selectedFolder] || "conversations";
    const selectedConversation = useMemo(
        () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
        [conversations, selectedConversationId],
    );
    const accountConversations = useMemo(
        () => addAccountIdentity(conversations, contacts),
        [conversations, contacts],
    );

    async function refreshGraphStatus() {
        setGraphStatusLoading(true);
        setGraphStatusError("");

        try {
            const payload = await getGraphStatus();
            setGraphStatus(payload || null);
        } catch (err) {
            setGraphStatus(null);
            setGraphStatusError(err?.message || "Failed to load Microsoft connection status");
        } finally {
            setGraphStatusLoading(false);
        }
    }

    useEffect(() => {
        refreshGraphStatus();
    }, []);

    useEffect(() => {
        let cancelled = false;

        getDeals()
            .then((rows) => {
                if (!cancelled) {
                    setDeals(Array.isArray(rows) ? rows : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDeals([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        getContacts()
            .then((rows) => {
                if (!cancelled) {
                    setContacts(Array.isArray(rows) ? rows : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    async function refreshPhoneCalls() {
        setLoadingPhoneCalls(true);
        setPhoneCallsError("");
        try {
            const payload = await getPhoneCalls();
            setPhoneCalls(Array.isArray(payload?.calls) ? payload.calls : []);
            setPhoneMetrics(payload?.metrics || {
                my_daily: 0,
                my_weekly: 0,
                team_daily: 0,
                team_weekly: 0,
                daily_goal: 60,
                weekly_goal: 300,
            });
        } catch (err) {
            setPhoneCalls([]);
            setPhoneCallsError(err?.message || "Failed to load phone calls");
        } finally {
            setLoadingPhoneCalls(false);
        }
    }

    useEffect(() => {
        if (selectedPanelType !== "phone" && currentView === "conversations") {
            return;
        }
        refreshPhoneCalls();
    }, [currentView, selectedPanelType]);

    useEffect(() => {
        if (!historyContactId) {
            setContactMessages([]);
            setContactMessagesError("");
            setLoadingContactMessages(false);
            return;
        }

        let cancelled = false;
        setLoadingContactMessages(true);
        setContactMessagesError("");

        getContactMessages(historyContactId)
            .then((rows) => {
                if (!cancelled) {
                    setContactMessages(Array.isArray(rows) ? rows : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setContactMessages([]);
                    setContactMessagesError(err?.message || "Failed to load email history");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingContactMessages(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [historyContactId]);

    useEffect(() => {
        setSelectedConversationId(null);
        setMessages([]);
        setMessagesError("");

        if (selectedPanelType !== "conversations") {
            setConversations([]);
            setConversationsError("");
            setLoadingConversations(false);
            setTotalPages(0);
            return;
        }

        let cancelled = false;
        setLoadingConversations(true);
        setConversationsError("");

        getConversations({
            folder: selectedFolder,
            page,
            pageSize,
            query: searchQuery,
            sort: sortBy,
        })
            .then((data) => {
                if (!cancelled) {
                    setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
                    setTotalPages(Number(data?.pagination?.total_pages || 0));
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setConversations([]);
                    setConversationsError(err?.message || "Failed to load conversations");
                    setTotalPages(0);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingConversations(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedFolder, selectedPanelType, page, pageSize, searchQuery, sortBy]);

    useEffect(() => {
        if (!selectedConversationId || selectedPanelType !== "conversations" || isComposeMode) {
            setMessages([]);
            setLoadingMessages(false);
            setMessagesError("");
            return;
        }

        let cancelled = false;
        setLoadingMessages(true);
        setMessagesError("");

        getConversationMessages(selectedConversationId)
            .then((data) => {
                if (!cancelled) {
                    setMessages(Array.isArray(data) ? data : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setMessages([]);
                    setMessagesError(err?.message || "Failed to load messages");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingMessages(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedConversationId, selectedPanelType, isComposeMode]);





    const {
        handleFolderClick,
        handleSearchQueryChange,
        handleSortChange,
        handlePrevPage,
        handleNextPage,
        handleConnectGraph,
        handleDisconnectGraph,
        handleComposeFormChange,
        handleStartCompose,
        handleReplyFromMessage,
        handleReplyAllFromMessage,
        handleForwardFromMessage,
        handleCancelCompose,
        handleSendMail,
    } = createConversationHandlers({
        selectedFolder,
        totalPages,
        graphStatus,
        mailForm,
        selectedConversation,
        setSearchQuery,
        setPage,
        setSelectedFolder,
        setIsComposeMode,
        setSortBy,
        setConnectingGraph,
        setGraphStatusError,
        setDisconnectingGraph,
        setMailFeedback,
        setMessagesError,
        setSelectedConversationId,
        setSendingMail,
        setMailForm,
        getGraphConnectURL,
        disconnectGraph,
        refreshGraphStatus,
        sendGraphMail,
    });

    const panelContentMap = {
        conversations: (
            <ConversationListPanel
                folderLabel={folderLabelMap[selectedFolder] || "Conversations"}
                loading={loadingConversations}
                error={conversationsError}
                conversations={accountConversations}
                selectedConversationId={selectedConversationId}
                onSelectConversation={(conversationId) => {
                    setIsComposeMode(false);
                    setSelectedConversationId(conversationId);
                }}
                messagesLoading={loadingMessages}
                messagesError={messagesError}
                messages={messages}
                searchQuery={searchQuery}
                onSearchQueryChange={handleSearchQueryChange}
                page={page}
                totalPages={totalPages}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                isComposeMode={isComposeMode}
                onStartCompose={handleStartCompose}
                composeForm={mailForm}
                onComposeFormChange={handleComposeFormChange}
                onSendCompose={handleSendMail}
                onCancelCompose={handleCancelCompose}
                sendingCompose={sendingMail}
                composeFeedback={mailFeedback}
                onReplyMessage={handleReplyFromMessage}
                onReplyAllMessage={handleReplyAllFromMessage}
                onForwardMessage={handleForwardFromMessage}
            />
        ),
        phone: (
            <PhoneCallsPanel
                calls={phoneCalls}
                metrics={phoneMetrics}
                deals={deals}
                contacts={contacts}
                onRefresh={refreshPhoneCalls}
                loading={loadingPhoneCalls}
                error={phoneCallsError}
                selectedFolder={selectedFolder}
            />
        ),
    };

    return (
        <Page>
            <div className="crm-conv-header" role="tablist" aria-label="Conversation page views">
                <button id="conversation-tab-conversations" type="button" className={currentView === "conversations" ? "is-active" : ""} onClick={() => setCurrentView("conversations")} role="tab" aria-controls="conversation-panel" aria-selected={currentView === "conversations"}>
                    <FiMessageSquare /> Messages &amp; Calls
                </button>
                <button id="conversation-tab-performance" type="button" className={currentView === "performance" ? "is-active" : ""} onClick={() => setCurrentView("performance")} role="tab" aria-controls="conversation-panel" aria-selected={currentView === "performance"}>
                    <FiBarChart2 /> Performance
                </button>
                <button id="conversation-tab-contacts" type="button" className={currentView === "contacts" ? "is-active" : ""} onClick={() => setCurrentView("contacts")} role="tab" aria-controls="conversation-panel" aria-selected={currentView === "contacts"}>
                    <FiUsers /> Contacts
                </button>
            </div>
            <div className="crm-conv-layout">
                {currentView === "conversations" ? (
                    <ConversationFiltersPanel
                        graphStatusLoading={graphStatusLoading}
                        graphStatusError={graphStatusError}
                        graphStatus={graphStatus}
                        connectingGraph={connectingGraph}
                        disconnectingGraph={disconnectingGraph}
                        onConnectGraph={handleConnectGraph}
                        onDisconnectGraph={handleDisconnectGraph}
                        emailFolders={EMAIL_FOLDERS}
                        phoneFolders={PHONE_FOLDERS}
                        selectedFolder={selectedFolder}
                        onFolderClick={handleFolderClick}
                    />
                ) : null}

                <div id="conversation-panel" className="crm-conv-display" role="tabpanel" aria-labelledby={`conversation-tab-${currentView}`}>
                    {currentView === "conversations" ? panelContentMap[selectedPanelType] : null}
                    {currentView === "performance" ? (
                        <PerformanceWidget calls={phoneCalls} metrics={phoneMetrics} loading={loadingPhoneCalls} error={phoneCallsError} />
                    ) : null}
                    {currentView === "contacts" ? (
                        <ContactsWidget
                            contacts={contacts}
                            calls={phoneCalls}
                            emails={contactMessages}
                            deals={deals}
                            loading={loadingPhoneCalls || loadingContactMessages}
                            error={phoneCallsError || contactMessagesError}
                            onContactSelect={(contact) => {
                                setContactMessages([]);
                                setHistoryContactId(contact?.id || null);
                            }}
                            onRefresh={refreshPhoneCalls}
                        />
                    ) : null}
                </div>
            </div>
        </Page>
    )
}


export default Conversations;